import type { ShowProductsMessage } from '@coldpress-voice/shared-types';
import { create } from 'zustand';
import type { CategoryGridItem } from './lib/categoryGrid';
import {
  type ContactFieldMap,
  type ContactFieldName,
  type ContactFieldStatus,
  applyCardScan,
  applyTypedField,
  mergeContactField,
} from './lib/contactFields';
import { type StyleSnapshot, parseEndScenePayload } from './lib/endScene';
import { type OrderItemMeta, parseOrderReviewItems, parseOrderReviewQty } from './lib/orderReview';
import {
  INITIAL_SCENE_STATE,
  type SceneName,
  applyLocalScene,
  applyServerScene,
  connectScene,
} from './lib/scene';
import type { StyleFeedback } from './lib/styleFeedback';
import type { GapNote, Handoff } from './lib/synergyR11';

/** PWA session state. Platform-tier (no project identity). Cards come from the agent's
 *  `ui.show_products` WS app-messages; transcript + audio levels from the Pipecat client callbacks
 *  (ADR-002 / item 1). `userLevel`/`botLevel` drive the voice-synced animation (frontend-spec). */

/**
 * Voice session status.
 *  - 'idle': no session — home screen shown.
 *  - 'warming': warm-up handshake in progress (R18 Wave 1.6 — polling /v1/session/warmup
 *    before opening the voice WS, so the container is already warm when the WS arrives).
 *    Governance overlays MUST NOT show. The WarmingScreen covers this state.
 *  - 'warmup-timeout': warm-up exceeded the budget (~55s) — graceful retry affordance shown.
 *  - 'connecting': WS handshake in progress (only entered after warm-up succeeds).
 *  - 'connected': WS up, voice session live.
 *  - 'error': connect failed — tap to retry.
 *  - 'dropped': WS dropped unexpectedly mid-session — reconnect/start-over UI shown.
 *    Governance overlays MUST NOT show in 'dropped' state.
 *    (Bug 1 fix — round-8-voice: onDisconnected without prior user hang-up → 'dropped',
 *     not 'idle', so the UI shows "reconnect" rather than a zombie governance overlay.)
 *  - 'reconnecting': R18 Wave 1.7 — auto-reconnect in progress (bounded retry after unexpected
 *    drop). A small "Reconnecting…" indicator is shown; the dropped panel is hidden until
 *    all retries are exhausted. Governance overlays MUST NOT show in this state.
 */
export type Status =
  | 'idle'
  | 'warming'
  | 'warmup-timeout'
  | 'connecting'
  | 'connected'
  | 'error'
  | 'dropped'
  | 'reconnecting';

export type TranscriptLine = {
  id: string;
  speaker: 'visitor' | 'agent';
  text: string;
  final: boolean;
};

/**
 * Serialisable governance event (no handlers — those are attached at render time in VoiceDemo).
 * Mirrors the GovernanceEvent union from SessionGovernanceOverlay but without the callbacks,
 * so it can live safely in the Zustand store.
 *
 * Populated by useVoice.ts `_handleGovernanceMessage` when the server sends `ui.governance`
 * WS app-messages (governance-contract.md). Wired in butler-r5.
 */
export type StoredGovernanceEvent =
  | { type: 'still-there' }
  | { type: 'time-warning'; secondsLeft: number; canExtend?: boolean }
  | {
      type: 'quality-guard';
      suggestion: 'push-to-talk' | 'type' | 'mute-background';
    };

type DemoState = {
  status: Status;
  error?: string;
  cards?: ShowProductsMessage;
  transcript: TranscriptLine[];
  micEnabled: boolean;
  paused: boolean;
  language: string; // active reply-language label shown on the chip (default English)
  languageForced: boolean; // true once the visitor pins one from the dropdown
  conversationId?: string; // emitted by the agent on connect — used to attach an uploaded card
  userLevel: number; // 0..1 visitor mic amplitude (voice-synced animation)
  botLevel: number; // 0..1 agent TTS amplitude

  /**
   * Active governance event from the agent (session governance §6d).
   * Null = no overlay. Set by setGovernanceEvent; cleared by clearGovernanceEvent.
   */
  governanceEvent: StoredGovernanceEvent | null;
  /**
   * Seconds remaining in the session — drives TimeRemainingCue.
   * Null = session not time-bounded (or cap not yet received from server).
   */
  secondsLeft: number | null;

  /** SKUs the visitor has tapped to shortlist (tap-to-select on showcase cards). */
  selectedSkus: string[];
  setSelectedSkus: (skus: string[]) => void;

  /**
   * Server-acknowledged selection: SKU → display name, populated by ui.selection_ack messages
   * (voice-client-contract.md §4). The chip tray is driven from this set so it reflects only
   * server-confirmed taps, not purely optimistic local state. The two sets converge quickly
   * (the ack round-trip is sub-second) but the tray shows real server state.
   *
   * R18 Wave 1.5 P1: ackedSelections drives the chip tray; selectedSkus stays as the optimistic
   * local set used by ShowcasePanel for immediate visual feedback on the card toggle.
   */
  ackedSelections: Record<string, string>; // sku → display name
  applySelectionAck: (sku: string, name: string, action: 'add' | 'remove') => void;

  /**
   * Close-card email ack (R12 — was `contactFormAck`; the standalone ContactCaptureForm was
   * retired). Drives the S7 close card's sent/queued state: null = submitted / in-flight,
   * true = persisted (send queued/sent), false = backend error. Set from `ui.contact_ack`.
   */
  closeEmailAck: boolean | null; // null = not submitted yet / in-flight
  setCloseEmailAck: (ack: boolean | null) => void;

  /**
   * R14 (B3) — the richer spec-sheet send state, layered over `closeEmailAck`:
   *   null       = not submitted yet / in-flight
   *   'sent'     = persisted + delivered/queued (truthful "on its way")
   *   'queued'   = persisted, delivery deferred (Resend not yet provisioned)
   *   'no_order' = send attempted with NO committed order this session → "confirm your selection
   *                first" (never a false confirmation). Distinct from an error.
   *   'error'    = backend rejected the submit
   * Set from `ui.contact_ack` (parsed tolerantly incl. a `no_order` state string, lib/sendState.ts).
   */
  specSheetSendState: import('./lib/sendState').SendState;
  setSpecSheetSendState: (s: import('./lib/sendState').SendState) => void;

  /**
   * R14 (B2) — a voice-confirmed order commit arrived (`ui.order_committed`, R31 §12). When true the
   * showcase advances the S6 review scene to the S7 close card exactly as if Confirm had been tapped
   * (voice-or-tap parity). Consumed + reset by the ShowcaseScene effect. Cleared on session reset.
   */
  orderCommitted: boolean;
  setOrderCommitted: (v: boolean) => void;

  /** Whether this session is being recorded (from ui.session_config WS message). */
  sessionRecording: boolean;
  setSessionRecording: (v: boolean) => void;

  // ── R10 — showcase scene model (showcase-conversation-synergy.md §3) ─────────
  /**
   * The scene currently on stage in the showcase panel. Driven by the server `ui.scene`
   * message AND by local visitor touch (touch is a full peer to voice). `welcomeDismissed`
   * makes the S1 skip sticky so the server can't re-trap a "just browsing" visitor.
   * Transition rules live in lib/scene.ts (pure, unit-tested).
   */
  scene: SceneName;
  welcomeDismissed: boolean;
  /**
   * R30 V2 — the S7 close-FIELD guard. True while a close-card field (email/phone) is focused with a
   * non-empty draft. While true, `applyServerScene` refuses to leave 'close' (the dev-117 glitch: a
   * server `ui.scene:'end'` swapping the close card for the walkthrough mid-typing). Client-local —
   * set/cleared by CloseScene on focus+draft / blur+submit+empty. Never gates local touch (Done).
   */
  closeFieldGuardActive: boolean;
  setCloseFieldGuard: (active: boolean) => void;
  /** Server-driven scene change (`ui.scene`). Honours welcome-dismissed stickiness + the V2 guard. */
  applyServerScene: (next: SceneName) => void;
  /** Local touch-driven scene change (always allowed; advancing past welcome dismisses it). */
  setScene: (next: SceneName) => void;
  /**
   * R15 T1 — the connect transition: leave the S0 attract resting scene for the S1 welcome contact
   * card (the first in-call beat). No-op if already past attract (reconnect/resume mid-session).
   */
  connectScene: () => void;

  /**
   * R10 — S1 contact-field capture map (name/company only, §4.1). Filled by three modes:
   * ui.contact_field voice/card echoes, card-scan results, and hand typing (§4.2).
   * Draft values render with a distinct treatment until final. Merge precedence in
   * lib/contactFields.ts (typed final is sacred — voice never clobbers it).
   */
  contactFields: ContactFieldMap;
  /** Apply an incoming ui.contact_field echo (voice or card source). */
  applyContactFieldEcho: (
    field: ContactFieldName,
    value: string,
    source: 'voice' | 'card',
    status: ContactFieldStatus
  ) => void;
  /** Apply a full card-scan result (fills fields live, source:'card', status:'final'). */
  applyCardScanFields: (fields: Partial<Record<ContactFieldName, string>>) => void;
  /** Apply a hand-typed edit (always authoritative typed final). */
  setTypedContactField: (field: ContactFieldName, value: string) => void;

  /**
   * R10 — deixis highlight. SKUs the server asked to pulse (`ui.highlight {skus}`) as Gigi
   * narrates them. Cleared after the pulse plays (the showcase manages the timeout).
   */
  highlightSkus: string[];
  setHighlightSkus: (skus: string[]) => void;

  /**
   * R22 (K2) — GENERALIZED control-pulse. A per-control monotonic nonce map bumped each time the
   * server emits a `ui.pulse_control {control}` cue (client contract §31, bound to concierge R43
   * server §31): when Gigi needs the visitor to act on a control (an out-of-flow email via `type`, or
   * "tap the pulsing Card button" via `card`) she directs them AND asks the kiosk to draw the eye to
   * it. Keyed by control name so a fresh value re-arms that ONE control's breathe (persistent-flavour
   * mechanics: brass-ring breathe until interaction or the 30s ceiling; reduced-motion static).
   * Consumers watch their control's value CHANGE, not its magnitude. A missing key = never pulsed (0).
   * Cleared on reset.
   *
   * R23 C3 — `card` is now a key in THIS map (the unified §31 pulseable set), so the Card and Type
   * pulses share one code path. The legacy bare `ui.pulse_card` cue (§23) is a tolerated alias:
   * `pulseCard()` below simply bumps the `card` key here (same nonce), so there is one channel.
   */
  pulseControlNonce: Record<string, number>;
  /** Bump a control's pulse nonce (from `ui.pulse_control {control}`, §31) — re-arms its breathe. */
  pulseControl: (control: string) => void;
  /**
   * Legacy alias (R23 C3): the bare `ui.pulse_card` cue (§23) maps to the `card` key of
   * `pulseControlNonce` — same nonce, one code path. Kept so the older server emission still works.
   */
  pulseCard: () => void;

  /** R10 — S6 order confirmed this session (drives the close/email treatment). */
  orderConfirmed: boolean;
  setOrderConfirmed: (v: boolean) => void;

  // R22 K3 — the R21 (X1) `emailCaptureNonce` / `requestEmailCaptureOverEnd` (email-capture-over-end
  // fallback) are RETIRED (USER-SPECIFIED). The special email screen is not part of the flow: the Type
  // composer is the ONE out-of-flow capture channel (§29 / K2), so a `capture_email` outside S7 pulses
  // the Type control rather than mounting a card over the goodbye. (The fallback was also unreachable:
  // the server's `emit_scene` terminal-end guard swallowed the `close` emission before the wire once
  // `end` was current — dev-88 turns 47–53, ×3.)

  // ── R12 — showcase scene stickiness + category grid + welcome auto-advance ────
  /**
   * R12 (F3 stomp-proof) — sticky once the order reaches the 'close' scene. Mirrors
   * SceneState.orderClosed; a server `ui.scene {browse}` after commit is ignored so a queued
   * post-commit browse render can't stomp the spec-sheet/close card (the field bug).
   */
  orderClosed: boolean;

  /**
   * R13 (U1) — sticky once the visitor has finished and reached the S9 'end' scene (style-snapshot
   * reveal + thanks). TERMINAL: no scene change leaves 'end' — only `reset()` (the "start a new
   * conversation" affordance) clears it. Mirrors SceneState.sessionEnded.
   */
  sessionEnded: boolean;

  /**
   * R13 (U1) — the style-snapshot the S9 end scene reveals. Parsed tolerantly from the server's
   * `ui.scene {scene:'end', payload}` (lib/endScene.ts). Null until the end scene arrives; the end
   * scene renders even with an empty snapshot (thanks + feedback don't depend on it).
   */
  endSnapshot: StyleSnapshot | null;
  /**
   * R14 (B1) — the raw `ui.scene {scene:'end'}` payload, retained so the in-call style-feedback
   * walkthrough can parse the additional per-facet palette tolerantly (lib/styleFeedback.ts). Null
   * until the end scene arrives; a missing payload falls back to the static palette.
   */
  endRawPayload: unknown;
  /** Set the end-scene snapshot AND raw payload from a raw `ui.scene {scene:'end'}` payload. */
  setEndScenePayload: (payload: unknown) => void;

  /**
   * R16 V3 — the S9 end-scene style-feedback the visitor has TOUCHED but not yet confirmed. The
   * EndScene mirrors its live {kept, removed, added} diff here on every chip toggle and clears it on
   * an explicit Confirm. On session wind-down (hang-up / new-conversation / disconnect) useVoice's
   * `disconnect()` flushes this — a best-effort auto-submit — BEFORE the WS client is torn down, so
   * an interacted-but-unconfirmed style read still lands (dev-77 M3: the visitor edited chips but
   * never tapped Confirm → declared_prefs stayed None). Null = nothing pending (untouched / already
   * confirmed). Cleared on reset.
   */
  pendingStyleFeedback: StyleFeedback | null;
  setPendingStyleFeedback: (feedback: StyleFeedback | null) => void;

  /**
   * R13 (U4) — server-provided per-item quantity defaults for the S6 order-review scene (voice
   * order: "one sample each" / "two of the first"). Parsed tolerantly from the `ui.scene
   * {scene:'order_review', payload}` (lib/orderReview.ts) and used to seed the steppers so the
   * visitor adjusts before Confirm. Empty map = default every line to 1. Cleared on reset.
   */
  orderReviewQty: Record<string, number>;
  /** Set the order-review qty defaults from a raw `ui.scene {scene:'order_review'}` payload. */
  setOrderReviewQty: (payload: unknown) => void;

  /**
   * R18 (X1) — server-resolved per-item name/image for the S6/S7 order lines (contract §14 W3
   * extension: `order_review` + `ui.order_committed` items become `{sku, qty, name, image}`). The
   * FIRST source `buildOrderLines` uses for a line's label/thumbnail (card cache second, raw SKU
   * last). Field evidence (dev-80): a voice-driven order whose cards had scrolled off rendered as
   * "Planters piece" — the names existed server-side. Merged (not replaced) across the
   * `order_review` scene and the later `ui.order_committed` so a name resolved on either message
   * survives. Empty = no server names (the cache/SKU path stands). Cleared on reset.
   */
  orderReviewItems: Record<string, OrderItemMeta>;
  /**
   * Merge server-resolved item name/image from a raw `order_review` scene payload OR a
   * `ui.order_committed` message into the per-SKU meta map (parsed tolerantly; later non-blank
   * values win per SKU, so a name resolved on commit refines an earlier one).
   */
  mergeOrderReviewItems: (payload: unknown) => void;

  /**
   * R12 (F2) — category tile grid for the showcase ("show me the categories"). When set, the
   * browse scene renders a 12-tile category grid instead of (or above) the product cards. Filled
   * from the server render path (`ui.show_categories` or a `mode:'categories'` render) parsed by
   * lib/categoryGrid.ts; also settable locally so a voice "show categories" with no server payload
   * still SHOWS the grid (never narration-only). Cleared when real product cards arrive.
   */
  categoryGrid: CategoryGridItem[] | null;
  setCategoryGrid: (grid: CategoryGridItem[] | null) => void;

  /**
   * R17 Q2 — the LAST category grid the server pushed, buffered so a payload-before-scene race can't
   * lose it. Field evidence (dev-79 turns 8–9, "I don't see any categories"): the post-confirm
   * auto-`show_categories` fired while the showcase was still on the welcome card; the `categoryGrid`
   * was set but never rendered (the browse branch only runs once the scene reaches browse), and an
   * interleaved render / `setCards` could clear it before the scene arrived. This buffer holds the
   * grid independently of the ephemeral `categoryGrid` so the browse scene can re-apply it on
   * arrival (ShowcaseScene). `applyBufferedCategories()` is idempotent — re-emitting the same grid
   * is a no-op-shaped re-set. Cleared on reset AND when real product cards arrive (the visitor moved
   * past the category grid to a specific category's pieces). Null = nothing buffered.
   */
  bufferedCategories: CategoryGridItem[] | null;
  /** Buffer + show a freshly-arrived category grid (called from the WS handler on ui.categories). */
  receiveCategories: (grid: CategoryGridItem[]) => void;
  /** Re-apply the buffered grid to `categoryGrid` (called when the browse scene mounts). Idempotent. */
  applyBufferedCategories: () => void;

  /**
   * R12 (F4) — welcome capture completed signal. Set true when the server signals welcome capture
   * is done (a scene:'browse' or a captured ack) OR the local ~2s-after-both-final fallback fires.
   * Drives the welcome card's captured/confirmed treatment + auto-advance so the card never lingers
   * blank. Reset on session reset.
   */
  welcomeCaptured: boolean;
  setWelcomeCaptured: (v: boolean) => void;

  // ── R11 — Synergy Round B (showcase-conversation-synergy.md §6 #5/#6) ────────
  /**
   * R11 #6 — knowledge-gap "noted" card. Set from `ui.gap_noted {topic}` when Gigi escalates a
   * question she can't answer ("that's one for the team"). Renders as a quiet, service-not-failure
   * overlay chip on the showcase that auto-dismisses. Null = nothing to show. Additive to the store
   * — a session that never escalates never touches this field.
   */
  gapNote: GapNote | null;
  setGapNote: (note: GapNote | null) => void;

  /**
   * R11 #5 — S8 floor handoff. Set from `ui.handoff {status, staffName?}` when the visitor asks to
   * be walked to a piece on the stand. `requested` → "Finding a colleague…"; `assigned`/`arriving`
   * → "{staffName} is on the way ✓". Persistent-but-unobtrusive until dismissed or the session
   * resets. Null = no active handoff.
   */
  handoff: Handoff | null;
  setHandoff: (handoff: Handoff | null) => void;

  setStatus: (s: Status, error?: string) => void;
  setCards: (cards: ShowProductsMessage) => void;
  upsertLine: (line: TranscriptLine) => void;
  setMic: (on: boolean) => void;
  setPaused: (paused: boolean) => void;
  setLanguage: (language: string, forced: boolean) => void;
  setConversationId: (id: string) => void;
  setLevels: (levels: { user?: number; bot?: number }) => void;
  /** Set/replace the current governance event (called by WS adapter in useVoice). */
  setGovernanceEvent: (event: StoredGovernanceEvent) => void;
  /** Dismiss the current governance event (called by overlay action handlers at render time). */
  clearGovernanceEvent: () => void;
  /** Update seconds remaining (driven by timer tick or WS caps message). */
  setSecondsLeft: (s: number | null) => void;
  /**
   * RC-2 (r18-reconnect-audiodebug): mark all non-final transcript lines as final.
   * Called at reconnect start so partial bubbles from the dropped session are closed
   * (they will never receive a completion callback from the dead client). New lines
   * from the reconnected client are appended normally with fresh IDs via upsertLine.
   */
  finalizePartialLines: () => void;
  reset: () => void;
};

export const useDemoStore = create<DemoState>((set, get) => ({
  status: 'idle',
  transcript: [],
  micEnabled: false,
  paused: false,
  language: 'English',
  languageForced: false,
  userLevel: 0,
  botLevel: 0,
  governanceEvent: null,
  secondsLeft: null,
  selectedSkus: [],
  ackedSelections: {},
  closeEmailAck: null,
  specSheetSendState: null,
  orderCommitted: false,
  sessionRecording: false,
  scene: INITIAL_SCENE_STATE.scene,
  welcomeDismissed: INITIAL_SCENE_STATE.welcomeDismissed,
  orderClosed: INITIAL_SCENE_STATE.orderClosed,
  sessionEnded: INITIAL_SCENE_STATE.sessionEnded,
  closeFieldGuardActive: false,
  endSnapshot: null,
  endRawPayload: null,
  pendingStyleFeedback: null,
  orderReviewQty: {},
  orderReviewItems: {},
  categoryGrid: null,
  bufferedCategories: null,
  welcomeCaptured: false,
  contactFields: {},
  highlightSkus: [],
  pulseControlNonce: {},
  orderConfirmed: false,
  gapNote: null,
  handoff: null,

  setStatus: (status, error) => set({ status, error }),
  setEndScenePayload: (payload) =>
    set({ endSnapshot: parseEndScenePayload(payload), endRawPayload: payload }),
  setSpecSheetSendState: (specSheetSendState) => set({ specSheetSendState }),
  setPendingStyleFeedback: (pendingStyleFeedback) => set({ pendingStyleFeedback }),
  setOrderCommitted: (orderCommitted) => set({ orderCommitted }),
  setOrderReviewQty: (payload) => set({ orderReviewQty: parseOrderReviewQty(payload) }),
  // R18 X1 — merge (never replace) so a name resolved on the `order_review` scene survives the later
  // `ui.order_committed`, and vice versa. Per SKU a later non-blank name/image wins.
  mergeOrderReviewItems: (payload) =>
    set((st) => {
      const incoming = parseOrderReviewItems(payload);
      if (Object.keys(incoming).length === 0) return st; // nothing to merge
      const merged: Record<string, OrderItemMeta> = { ...st.orderReviewItems };
      for (const [sku, meta] of Object.entries(incoming)) {
        merged[sku] = { ...merged[sku], ...meta };
      }
      return { orderReviewItems: merged };
    }),
  // Real product cards arriving supersede a category grid (F2): the visitor picked a category and
  // the server pushed its pieces. Clearing categoryGrid AND the R17 Q2 buffer lets the browse scene
  // show the products (and stops a stale buffer re-showing categories over the picked pieces).
  setCards: (cards) => set({ cards, categoryGrid: null, bufferedCategories: null }),
  upsertLine: (line) =>
    set((st) => {
      const i = st.transcript.findIndex((l) => l.id === line.id);
      if (i === -1) return { transcript: [...st.transcript, line] };
      const next = st.transcript.slice();
      next[i] = line;
      return { transcript: next };
    }),
  setMic: (micEnabled) => set({ micEnabled }),
  setPaused: (paused) => set({ paused }),
  // Don't let auto-detect override a language the visitor explicitly pinned.
  setLanguage: (language, forced) =>
    set((st) => (!forced && st.languageForced ? {} : { language, languageForced: forced })),
  setConversationId: (conversationId) => set({ conversationId }),
  setLevels: ({ user, bot }) =>
    set((st) => ({
      userLevel: user ?? st.userLevel,
      botLevel: bot ?? st.botLevel,
    })),
  setGovernanceEvent: (governanceEvent) => set({ governanceEvent }),
  clearGovernanceEvent: () => set({ governanceEvent: null }),
  setSecondsLeft: (secondsLeft) => set({ secondsLeft }),
  finalizePartialLines: () =>
    set((st) => {
      const hasPartial = st.transcript.some((l) => !l.final);
      if (!hasPartial) return {};
      return { transcript: st.transcript.map((l) => (l.final ? l : { ...l, final: true })) };
    }),
  setSelectedSkus: (selectedSkus) => set({ selectedSkus }),
  applySelectionAck: (sku, name, action) =>
    set((st) => {
      const next = { ...st.ackedSelections };
      if (action === 'add') {
        next[sku] = name;
      } else {
        delete next[sku];
      }
      return { ackedSelections: next };
    }),
  setCloseEmailAck: (closeEmailAck) => set({ closeEmailAck }),
  setSessionRecording: (sessionRecording) => set({ sessionRecording }),
  setCategoryGrid: (categoryGrid) => set({ categoryGrid }),
  // R17 Q2 — buffer the grid AND show it now. If the scene is already on browse the `categoryGrid`
  // set renders immediately; if it's still on welcome/attract the buffer survives (and setCards
  // clears both once the visitor picks a category), so applyBufferedCategories() can re-show it when
  // the browse scene mounts.
  receiveCategories: (grid) => set({ categoryGrid: grid, bufferedCategories: grid }),
  // R17 Q2 — re-apply the buffered grid on browse-scene arrival. Idempotent: no buffer → no-op; a
  // grid already on stage (same reference) → the set is a no-op-shaped write. Never clobbers real
  // product cards — the caller only re-applies when no product grid is showing.
  applyBufferedCategories: () =>
    set((st) => (st.bufferedCategories ? { categoryGrid: st.bufferedCategories } : {})),
  setWelcomeCaptured: (welcomeCaptured) => set({ welcomeCaptured }),
  setCloseFieldGuard: (closeFieldGuardActive) => set({ closeFieldGuardActive }),
  applyServerScene: (next) =>
    set((st) =>
      applyServerScene(
        {
          scene: st.scene,
          welcomeDismissed: st.welcomeDismissed,
          orderClosed: st.orderClosed,
          sessionEnded: st.sessionEnded,
        },
        next,
        // R30 V2 — pass the live close-field guard so a server scene change can't leave 'close' while
        // the visitor is mid-typing on the close card (the dev-117 draft-destroying glitch).
        st.closeFieldGuardActive
      )
    ),
  setScene: (next) =>
    set((st) =>
      applyLocalScene(
        {
          scene: st.scene,
          welcomeDismissed: st.welcomeDismissed,
          orderClosed: st.orderClosed,
          sessionEnded: st.sessionEnded,
        },
        next
      )
    ),
  connectScene: () =>
    set((st) =>
      connectScene({
        scene: st.scene,
        welcomeDismissed: st.welcomeDismissed,
        orderClosed: st.orderClosed,
        sessionEnded: st.sessionEnded,
      })
    ),
  applyContactFieldEcho: (field, value, source, status) =>
    set((st) => ({
      contactFields: mergeContactField(st.contactFields, field, value, source, status),
    })),
  applyCardScanFields: (fields) =>
    set((st) => ({ contactFields: applyCardScan(st.contactFields, fields) })),
  setTypedContactField: (field, value) =>
    set((st) => ({ contactFields: applyTypedField(st.contactFields, field, value) })),
  setHighlightSkus: (highlightSkus) => set({ highlightSkus }),
  // R22 K2 / R23 C3 — bump ONE control's pulse nonce (immutably, so the ControlBar effect watching
  // that key re-runs). An absent key starts at 0; the first pulse yields 1; a repeat re-arms with a
  // fresh value. `card` and `type` are both keys here (unified §31 set).
  pulseControl: (control) =>
    set((s) => ({
      pulseControlNonce: {
        ...s.pulseControlNonce,
        [control]: (s.pulseControlNonce[control] ?? 0) + 1,
      },
    })),
  // R23 C3 — legacy `ui.pulse_card` alias → the SAME `card` nonce (one code path).
  pulseCard: () => get().pulseControl('card'),
  setOrderConfirmed: (orderConfirmed) => set({ orderConfirmed }),
  setGapNote: (gapNote) => set({ gapNote }),
  setHandoff: (handoff) => set({ handoff }),
  reset: () =>
    set({
      status: 'idle',
      error: undefined,
      cards: undefined,
      transcript: [],
      micEnabled: false,
      paused: false,
      language: 'English',
      languageForced: false,
      conversationId: undefined,
      userLevel: 0,
      botLevel: 0,
      governanceEvent: null,
      secondsLeft: null,
      selectedSkus: [],
      ackedSelections: {},
      closeEmailAck: null,
      specSheetSendState: null,
      orderCommitted: false,
      sessionRecording: false,
      scene: INITIAL_SCENE_STATE.scene,
      welcomeDismissed: INITIAL_SCENE_STATE.welcomeDismissed,
      orderClosed: INITIAL_SCENE_STATE.orderClosed,
      sessionEnded: INITIAL_SCENE_STATE.sessionEnded,
      closeFieldGuardActive: false,
      endSnapshot: null,
      endRawPayload: null,
      pendingStyleFeedback: null,
      orderReviewQty: {},
      orderReviewItems: {},
      categoryGrid: null,
      bufferedCategories: null,
      welcomeCaptured: false,
      contactFields: {},
      highlightSkus: [],
      pulseControlNonce: {},
      orderConfirmed: false,
      gapNote: null,
      handoff: null,
    }),
}));
